import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Form,
  DateField,
  TimePickerField,
  SingleSelectField,
  TextField,
  Text,
  SubmitButton,
  isMobile,
} from '@medely/ui-kit';
import { useFormContext } from '@medely/ui-kit/react-hook-form';
import moment from 'moment-timezone';
import { EDIT_CLOCK_TIMES_REASONS, REASON_OTHER_VALUE } from './constants';
import ErrorAlert from '@mp/components/errorAlert/ErrorAlert';
import { FixedFooterLayout } from '@mp/components/FixedFooterLayout';
import { ResultOf } from '@graphql-typed-document-node/core';
import { parseFragmentData } from '@mp/graphql/generated';
import { useKeyboardLayout } from '@mp/hooks';
import { useProNavigate } from '@mp/contexts';
import { JobDetailsFragment, useJobForJobDetailsContainer } from '../jobDetailsComponents/graphql';
import { useEditClockTimesMutation } from './mutations';
import { createEditClockTimesSchema, EditClockTimesFormValues } from './schema';
import {
  EditClockTimesInitialValues,
  getEditClockTimesDefaultValues,
  hasClockTimesChanged,
} from './utils';
import { formatTimePickerValue, getClockDateBoundaries, parseTimePickerValue } from './helper';

const IS_MOBILE = isMobile();

export const EditClockTimesFormContent = ({ jobId }: { jobId: number }) => {
  const { job: jobFromQuery } = useJobForJobDetailsContainer(jobId);
  const job = jobFromQuery ? parseFragmentData(JobDetailsFragment, jobFromQuery) : undefined;
  const timezone = job?.location?.timezone_lookup || moment.tz.guess();
  const { editClockTimes, isLoading, errorMessage } = useEditClockTimesMutation(jobId);
  const { navigate } = useProNavigate();
  const [initialValues, setInitialValues] = useState<EditClockTimesInitialValues>();

  const handleSubmit = (values: EditClockTimesFormValues) => {
    if (initialValues && !hasClockTimesChanged(values, initialValues, timezone)) {
      navigate('JobDetails', { id: jobId });
      return;
    }
    editClockTimes({ jobId, formValues: values, timezone });
  };

  const schema = createEditClockTimesSchema({
    startsTime: job?.starts_time,
    endsTime: job?.ends_time,
    timezone,
    initialValues,
  });

  return (
    <Form key="edit-clock-times" handleSubmit={handleSubmit} schema={schema}>
      <EditClockTimesFormBody
        job={job}
        initialValues={initialValues}
        onInitialValues={setInitialValues}
        isLoading={isLoading}
        errorMessage={errorMessage}
      />
    </Form>
  );
};

function EditClockTimesFormBody({
  job,
  initialValues,
  onInitialValues,
  isLoading,
  errorMessage,
}: {
  job: ResultOf<typeof JobDetailsFragment> | undefined;
  initialValues: EditClockTimesInitialValues | undefined;
  onInitialValues: (values: EditClockTimesInitialValues) => void;
  isLoading: boolean;
  errorMessage: string | null;
}) {
  const {
    watch,
    reset,
    setValue,
    clearErrors,
    formState: { errors },
  } = useFormContext<EditClockTimesFormValues>();
  const { isKeyboardVisible } = useKeyboardLayout();
  const initializedForJobId = useRef<number | null>(null);

  useEffect(() => {
    if (!job || initializedForJobId.current === job.id) {
      return;
    }
    initializedForJobId.current = job.id;
    const defaults = getEditClockTimesDefaultValues(job);
    onInitialValues(defaults);
    reset({
      ...defaults,
      reason: undefined,
      reason_other: undefined,
    } as Parameters<typeof reset>[0]);
  }, [job, reset, onInitialValues]);

  const tz = job?.location?.timezone_lookup || moment.tz.guess();
  const [clockInDate, clockInTime, clockOutDate, clockOutTime, reason] = watch([
    'approved_clock_in_date',
    'approved_clock_in_time',
    'approved_clock_out_date',
    'approved_clock_out_time',
    'reason',
  ]);
  const hasChanges =
    !!initialValues &&
    hasClockTimesChanged(
      {
        approved_clock_in_date: clockInDate,
        approved_clock_in_time: clockInTime,
        approved_clock_out_date: clockOutDate,
        approved_clock_out_time: clockOutTime,
      },
      initialValues,
      tz,
    );
  const showOtherReasonField = hasChanges && reason === REASON_OTHER_VALUE;

  // The reason only applies to an actual edit, so drop it if the times go back to the originals.
  useEffect(() => {
    if (hasChanges) {
      return;
    }
    setValue('reason', undefined);
    setValue('reason_other', undefined);
    clearErrors(['reason', 'reason_other']);
  }, [hasChanges, setValue, clearErrors]);

  const mobileTimePickerProps = IS_MOBILE
    ? {
        formatOnChange: (value: unknown, fieldValue: unknown) =>
          formatTimePickerValue(value, fieldValue, tz),
        parseValue: (value: unknown) => parseTimePickerValue(value, tz),
      }
    : {};
  const scheduleStartTime = job?.starts_time
    ? moment.tz(job.starts_time, tz).format('HH:mm')
    : null;
  const scheduleEndTime = job?.ends_time ? moment.tz(job.ends_time, tz).format('HH:mm') : null;
  const { clockInMinDate, clockInMaxDate, clockOutMinDate, clockOutMaxDate } =
    getClockDateBoundaries(job);

  const renderScheduleHelperText = (type: 'clock-in' | 'clock-out' = 'clock-in') => {
    const label = type === 'clock-in' ? 'start' : 'end';
    const time = type === 'clock-in' ? scheduleStartTime ?? '08:00' : scheduleEndTime ?? '20:00';

    return (
      <Box pl={1} pt={0.5}>
        <Text size="s" color="text.secondary">
          {`Schedule ${label} time ${time}`}
        </Text>
      </Box>
    );
  };

  return (
    <FixedFooterLayout
      autoScrollToEndOnKeyboardOpen={showOtherReasonField}
      footerContent={
        !isKeyboardVisible && (
          <Box
            bgColor="surface.paper"
            px={2}
            py={1}
            borderTopWidth={IS_MOBILE ? 1 : 0}
            borderColor="controls.backing+line"
          >
            <SubmitButton fullWidth disabled={isLoading} loading={isLoading}>
              Save
            </SubmitButton>
          </Box>
        )
      }
      height="100%"
    >
      <Box width="100%">
        <DateField
          label="Clock in date*"
          name="approved_clock_in_date"
          maxDate={clockInMaxDate}
          minDate={clockInMinDate}
        />
      </Box>
      <Box width="100%" mt={2}>
        <TimePickerField
          {...mobileTimePickerProps}
          military
          label="Clock in time*"
          name="approved_clock_in_time"
          timezone={tz}
        />
        {!errors.approved_clock_in_time && renderScheduleHelperText()}
      </Box>
      <Box width="100%" mt={2}>
        <DateField
          label="Clock out date*"
          name="approved_clock_out_date"
          maxDate={clockOutMaxDate}
          minDate={clockOutMinDate}
        />
      </Box>
      <Box width="100%" mt={2} mb={2}>
        <TimePickerField
          {...mobileTimePickerProps}
          military
          label="Clock out time*"
          name="approved_clock_out_time"
          timezone={tz}
        />
        {!errors.approved_clock_out_time && renderScheduleHelperText('clock-out')}
      </Box>
      <SingleSelectField
        variant="bottomSheet"
        name="reason"
        label="Reason*"
        disabled={!hasChanges}
        options={EDIT_CLOCK_TIMES_REASONS}
      />
      <Box mt={IS_MOBILE ? 0 : 4}>
        {showOtherReasonField && (
          <TextField
            multiline
            labelStyle="stacked"
            name="reason_other"
            placeholder="Tell us why"
            rows={4}
          />
        )}
      </Box>
      {errorMessage && (
        <Box my={2}>
          <ErrorAlert body={errorMessage} title="Error" />
        </Box>
      )}
    </FixedFooterLayout>
  );
}
